import { z } from 'zod';
import { activityLevelSchema, bmrFormula, genderEnumSchema } from "./enums";

export const goalBmrSettingsSchema = z
  .object({
    heightCm: z.number(),
    age: z.number(),
    gender: genderEnumSchema,
    activityLevel: activityLevelSchema,
    formula: bmrFormula,
    highMuscleContent: z.boolean(),
    useManual: z.boolean(),
    manualValue: z.number().nullable(),
  })
  ;
export type GoalBmrSettings = z.infer<typeof goalBmrSettingsSchema>;

// Per-intensity workout fuelling targets (g/kg bodyweight). Drives the loading
// (pre-workout carbs) and recovery (post-workout carbs + protein) targets the
// nutrition planner will consume (planner wiring is a later phase).
export const workoutFuelingIntensityTargetsSchema = z.object({
  loadingCarbsGPerKg: z.number(),
  recoveryCarbsGPerKg: z.number(),
  recoveryProteinGPerKg: z.number(),
});
export type WorkoutFuelingIntensityTargets = z.infer<typeof workoutFuelingIntensityTargetsSchema>;

export const workoutFuelingIntensityEnum = z.enum(['recovery', 'low', 'moderate', 'high', 'race']);
export type WorkoutFuelingIntensity = z.infer<typeof workoutFuelingIntensityEnum>;

export const goalWorkoutFuelingTargetsSchema = z.object({
  recovery: workoutFuelingIntensityTargetsSchema,
  low: workoutFuelingIntensityTargetsSchema,
  moderate: workoutFuelingIntensityTargetsSchema,
  high: workoutFuelingIntensityTargetsSchema,
  race: workoutFuelingIntensityTargetsSchema,
});
export type GoalWorkoutFuelingTargets = z.infer<typeof goalWorkoutFuelingTargetsSchema>;

export const goalSchema = z
  .object({
    id: z.string(),
    effectiveFromDate: z.string(),
    goalType: z.enum(['lose', 'maintain', 'gain', 'fixed']),
    caloriesTarget: z.number(),
    strategyType: z.enum(['percent', 'grams_per_kg', 'flex']),

    // Percentage strategy
    proteinPct: z.number().nullable().optional(),
    carbsPct: z.number().nullable().optional(),
    fatPct: z.number().nullable().optional(),

    // Grams per kg strategy
    proteinGPerKg: z.number().nullable().optional(),
    carbsGPerKg: z.number().nullable().optional(),
    fatGPerKg: z.number().nullable().optional(),
    flexMacro: z.enum(['carbs', 'fat']).nullable().optional(),

    // Fixed grams strategy
    fixedProteinG: z.number().nullable().optional(),
    fixedCarbsG: z.number().nullable().optional(),
    fixedFatG: z.number().nullable().optional(),

    // Embedded BMR settings
    bmrSettings: goalBmrSettingsSchema,

    // Per-intensity workout fuelling targets (g/kg). Optional/back-compat.
    workoutFuelingTargets: goalWorkoutFuelingTargetsSchema.nullable().optional(),

    createdAtUtc: z.string(),
    updatedAtUtc: z.string(),
  })
  ;
export type Goal = z.infer<typeof goalSchema>;

export const goalHistoryDocSchema = z.object({
  id: z.string(),
  userId: z.string(),
  effectiveFromDate: z.string(),
  goalType: z.enum(['lose', 'maintain', 'gain', 'fixed']),
  caloriesTarget: z.number(),
  strategyType: z.enum(['percent', 'grams_per_kg', 'flex']),
  proteinPct: z.number().nullable(),
  carbsPct: z.number().nullable(),
  fatPct: z.number().nullable(),
  proteinGPerKg: z.number().nullable(),
  carbsGPerKg: z.number().nullable(),
  fatGPerKg: z.number().nullable(),
  flexMacro: z.enum(['carbs', 'fat']).nullable(),
  fixedProteinG: z.number().nullable(),
  fixedCarbsG: z.number().nullable(),
  fixedFatG: z.number().nullable(),
  bmrSettings: goalBmrSettingsSchema,
  workoutFuelingTargets: goalWorkoutFuelingTargetsSchema.nullable().optional(),
  createdAtUtc: z.string(),
  updatedAtUtc: z.string(),
  deletedAt: z.string().nullable(),
});
export type GoalHistoryDocType = z.infer<typeof goalHistoryDocSchema>;

// Sensible per-intensity defaults used when a goal has no explicit targets.
export const DEFAULT_WORKOUT_FUELING_TARGETS: GoalWorkoutFuelingTargets = {
  recovery: { loadingCarbsGPerKg: 0, recoveryCarbsGPerKg: 0.5, recoveryProteinGPerKg: 0.3 },
  low: { loadingCarbsGPerKg: 0.5, recoveryCarbsGPerKg: 0.6, recoveryProteinGPerKg: 0.3 },
  moderate: { loadingCarbsGPerKg: 1.0, recoveryCarbsGPerKg: 0.8, recoveryProteinGPerKg: 0.35 },
  high: { loadingCarbsGPerKg: 1.5, recoveryCarbsGPerKg: 1.0, recoveryProteinGPerKg: 0.4 },
  race: { loadingCarbsGPerKg: 2.0, recoveryCarbsGPerKg: 1.2, recoveryProteinGPerKg: 0.4 },
};
